import { EvaluationCriteria, WorkflowCondition, ConditionOperator } from '../../types/workflowRules';

/**
 * Evaluates workflow rule criteria against a record
 */
export class CriteriaEvaluator {

  /**
   * Evaluate criteria against a record
   * Returns true if criteria are met, false otherwise
   */
  evaluate(criteria: EvaluationCriteria, record: any): boolean {
    // No conditions = always true (rule runs for all records)
    if (!criteria.conditions || criteria.conditions.length === 0) {
      return true;
    }

    const results = criteria.conditions.map(condition =>
      this.evaluateCondition(condition, record)
    );

    // Apply AND/OR logic
    if (criteria.operator === 'AND') {
      return results.every(result => result === true);
    } else {
      return results.some(result => result === true);
    }
  }

  /**
   * Evaluate a single condition
   */
  private evaluateCondition(condition: WorkflowCondition, record: any): boolean {
    const fieldValue = this.getFieldValue(record, condition.fieldApiName);
    const compareValue = condition.value;
    const operator = condition.operator;

    console.log(`🔍 Evaluating: ${condition.fieldApiName} ${operator} ${compareValue}`);
    console.log(`   Record value: ${fieldValue}`);

    switch (operator) {
      case 'equals':
        return this.compareEquals(fieldValue, compareValue);

      case 'notEquals':
        return !this.compareEquals(fieldValue, compareValue);

      case 'contains':
        return this.compareContains(fieldValue, compareValue);

      case 'doesNotContain':
        return !this.compareContains(fieldValue, compareValue);

      case 'startsWith':
        return this.compareStartsWith(fieldValue, compareValue);

      case 'endsWith':
        return this.compareEndsWith(fieldValue, compareValue);

      case 'lessThan':
        return this.compareLessThan(fieldValue, compareValue);

      case 'greaterThan':
        return this.compareGreaterThan(fieldValue, compareValue);

      case 'lessThanOrEqual':
        return this.compareLessThanOrEqual(fieldValue, compareValue);

      case 'greaterThanOrEqual':
        return this.compareGreaterThanOrEqual(fieldValue, compareValue);

      case 'isEmpty':
        return this.isEmpty(fieldValue);

      case 'isNotEmpty':
        return !this.isEmpty(fieldValue);

      default:
        console.warn(`Unknown operator: ${operator}`);
        return false;
    }
  }

  /**
   * Get field value from record (supports nested fields)
   */
  private getFieldValue(record: any, fieldApiName: string): any {
    if (!record) return undefined;

    // Support nested fields (e.g., "owner.name")
    const parts = fieldApiName.split('.');
    let value = record;

    for (const part of parts) {
      if (value === null || value === undefined) {
        return undefined;
      }
      value = value[part];
    }

    return value;
  }

  // ==========================================
  // COMPARISON METHODS
  // ==========================================

  private compareEquals(fieldValue: any, compareValue: any): boolean {
    // Normalize for comparison
    const normalized1 = this.normalizeValue(fieldValue);
    const normalized2 = this.normalizeValue(compareValue);

    return normalized1 === normalized2;
  }

  private compareContains(fieldValue: any, compareValue: any): boolean {
    if (this.isEmpty(fieldValue) || this.isEmpty(compareValue)) {
      return false;
    }

    const str = String(fieldValue).toLowerCase();
    const search = String(compareValue).toLowerCase();

    return str.includes(search);
  }

  private compareStartsWith(fieldValue: any, compareValue: any): boolean {
    if (this.isEmpty(fieldValue) || this.isEmpty(compareValue)) {
      return false;
    }

    const str = String(fieldValue).toLowerCase();
    const search = String(compareValue).toLowerCase();

    return str.startsWith(search);
  }

  private compareEndsWith(fieldValue: any, compareValue: any): boolean {
    if (this.isEmpty(fieldValue) || this.isEmpty(compareValue)) {
      return false;
    }

    const str = String(fieldValue).toLowerCase();
    const search = String(compareValue).toLowerCase();

    return str.endsWith(search);
  }

  private compareLessThan(fieldValue: any, compareValue: any): boolean {
    const num1 = this.toNumber(fieldValue);
    const num2 = this.toNumber(compareValue);

    if (num1 === null || num2 === null) {
      return false;
    }

    return num1 < num2;
  }

  private compareGreaterThan(fieldValue: any, compareValue: any): boolean {
    const num1 = this.toNumber(fieldValue);
    const num2 = this.toNumber(compareValue);

    if (num1 === null || num2 === null) {
      return false;
    }

    return num1 > num2;
  }

  private compareLessThanOrEqual(fieldValue: any, compareValue: any): boolean {
    const num1 = this.toNumber(fieldValue);
    const num2 = this.toNumber(compareValue);

    if (num1 === null || num2 === null) {
      return false;
    }

    return num1 <= num2;
  }

  private compareGreaterThanOrEqual(fieldValue: any, compareValue: any): boolean {
    const num1 = this.toNumber(fieldValue);
    const num2 = this.toNumber(compareValue);

    if (num1 === null || num2 === null) {
      return false;
    }

    return num1 >= num2;
  }

  private isEmpty(value: any): boolean {
    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value === 'string' && value.trim() === '') {
      return true;
    }

    if (Array.isArray(value) && value.length === 0) {
      return true;
    }

    return false;
  }

  // ==========================================
  // HELPER METHODS
  // ==========================================

  private normalizeValue(value: any): any {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'string') {
      return value.trim().toLowerCase();
    }

    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      return value;
    }

    return String(value).toLowerCase();
  }

  private toNumber(value: any): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    // If already a number
    if (typeof value === 'number') {
      return value;
    }

    // Try to parse as number
    const num = parseFloat(String(value));

    if (isNaN(num)) {
      return null;
    }

    return num;
  }
}

// Export singleton instance
export const criteriaEvaluator = new CriteriaEvaluator();
